import { type FC } from 'react';
import { useAtom } from 'jotai';
import { startCase } from 'lodash-es';
import { Button, InlineLoading } from '@carbon/react';
import { Edit } from '@carbon/react/icons';
import { formatNumber } from 'utils/number';
import type { PreDrillWellboreDetails } from 'types/api-responses';
import { fieldDate } from 'components/well-cards/field-grid';
import { useWellboreDetailsAtom, type WellboreDetailsAtom } from './atoms';
import { formatSrpWellName } from './utils';
import styles from './wellbore-details-card.module.scss';

interface WellboreDetailsCardProps {
  /** Sub-resource href from the thin show (`resources.wellboreDetails.href`). */
  href: string;
  /** The host's own read of the same resource, when it needs to re-read it (Story 5:
   *  the datum drawer changes what the card shows). Omitted, the card reads alone. */
  detailsAtom?: WellboreDetailsAtom;
  /** Story 5: opens the Property Details drawer. Omitted, the card is read-only. */
  onEditDetails?: () => void;
  /** Whether the Kelly Bushing is a derived figure rather than a filed one; the points
   *  read says. Omitted, it is treated as an estimate, which a permit-derived KB is. */
  kbElevationEstimated?: boolean;
}

interface DetailField {
  label: string;
  value: string;
}

function ft(value: number | null | undefined, estimated = false): string {
  if (value === null || value === undefined) return '—';
  return `${formatNumber(value, 0, 0)} ft${estimated ? ' (EST)' : ''}`;
}

function detailFields(
  details: PreDrillWellboreDetails,
  kbElevationEstimated: boolean
): DetailField[] {
  return [
    { label: 'UWI', value: details.uwi14 },
    {
      label: 'SRP Well Name',
      value:
        details.srpWellName ??
        formatSrpWellName(details.wellName, details.wellNum) ??
        '—',
    },
    { label: 'Operator', value: details.currentOperator ?? '—' },
    { label: 'Hole Direction', value: details.permitHoleDirection ?? '—' },
    {
      // Title-case the enum (e.g. "EXPIRED_PERMIT" → "Expired Permit");
      // lowercase first so all-caps don't stay shouting.
      label: 'Permit Activity Status',
      value: details.permitActivityStatus
        ? startCase(details.permitActivityStatus.toLowerCase())
        : '—',
    },
    // The same numeric US date the Well Status card shows for this field (mock: 9/24/2025).
    { label: 'Permit Date', value: fieldDate(details.permitDate) },
    {
      label: 'Permitted Formation',
      value: details.permitTargetFormation ?? '—',
    },
    { label: 'Permitted Depth', value: ft(details.permittedDepthFt) },
    {
      label: 'Permitted True Vertical Depth',
      value: ft(details.permittedDepthTvdFt),
    },
    { label: 'Lateral Length', value: ft(details.lateralLengthEstFt, true) },
    {
      label: 'Ground Level Elevation',
      value: ft(details.groundLevelElevationFt),
    },
    {
      label: 'Kelly Bushing Elevation (KB, ft)',
      value: ft(details.kbElevationFt, kbElevationEstimated),
    },
  ];
}

// Wellbore Details header card: fetches the `wellbore_details` sub-resource and
// renders a title over a 6-column label/value grid.
export const WellboreDetailsCard: FC<WellboreDetailsCardProps> = ({
  href,
  detailsAtom: sharedDetailsAtom,
  onEditDetails,
  kbElevationEstimated = true,
}) => {
  const standaloneDetailsAtom = useWellboreDetailsAtom(
    sharedDetailsAtom ? undefined : href
  );
  const [resource] = useAtom(sharedDetailsAtom ?? standaloneDetailsAtom);
  const details =
    resource.data && !('error' in resource.data) ? resource.data : null;

  return (
    <section className={styles.card}>
      <div className={styles.header}>
        <h2 className={styles.title}>Wellbore Details</h2>
        {onEditDetails && (
          <Button
            kind="ghost"
            size="sm"
            hasIconOnly
            renderIcon={Edit}
            iconDescription="Edit wellbore details"
            tooltipPosition="left"
            onClick={onEditDetails}
          />
        )}
      </div>
      {!details ? (
        <InlineLoading description="Loading wellbore details…" />
      ) : (
        <div className={styles.grid}>
          {detailFields(details, kbElevationEstimated).map((field) => (
            <div key={field.label} className={styles.field}>
              <span className={styles.label}>{field.label}</span>
              <span className={styles.value}>{field.value}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
